import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ShieldLogo } from "../components/landing/Logo";
import TrialExpirationModal from "../components/TrialExpirationModal";
import {
  Phone, Eye, ChefHat, BarChart3, Crown, Clock, 
  CheckCircle2, Circle, ArrowRight, Sparkles, Home,
  AlertTriangle, Lock, Loader2, Play, ExternalLink, CreditCard
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mission configuration
const ECHO_PHONE = process.env.REACT_APP_ECHO_PHONE || "";

const MISSIONS = [
  {
    id: "onboarding_completed",
    day: 0,
    title: "Set Up Your Restaurant",
    description: "Add your restaurant details, menu, and hours so supervisors can work for you",
    icon: Home,
    color: "gold",
    action: "Start Setup",
    path: "/onboarding",
    supervisor: null,
    priority: true
  },
  {
    id: "welcome_completed",
    day: 1,
    title: "Welcome to Argus Xenia",
    description: "Take a quick tour of your AI-powered restaurant OS",
    icon: Sparkles,
    color: "gold",
    action: "Start Tour",
    path: null, // Modal or inline
    supervisor: null
  },
  {
    id: "echo_tested",
    day: 1,
    title: "Meet Supervisor Echo",
    description: "Call our AI receptionist and test reservation handling",
    icon: Phone,
    color: "emerald",
    action: "Call Echo",
    phone: ECHO_PHONE,
    supervisor: "Echo"
  },
  {
    id: "apollo_tested",
    day: 2,
    title: "Activate Supervisor Apollo",
    description: "Upload a kitchen photo for AI vision analysis",
    icon: Eye,
    color: "blue",
    action: "Test Vision AI",
    path: "/trial/vision",
    supervisor: "Apollo"
  },
  {
    id: "athena_tested",
    day: 3,
    title: "Train Supervisor Athena",
    description: "Record a voice SOP and see cost analysis in action",
    icon: ChefHat,
    color: "orange",
    action: "Create SOP",
    path: "/trial/sop-builder",
    supervisor: "Athena"
  },
  {
    id: "pos_ready",
    day: 3,
    title: "POS Integration Coming Soon",
    description: "Connect Toast, Square, Clover, or Lightspeed — genuine sync available after trial",
    icon: CreditCard,
    color: "slate",
    action: null,
    path: null,
    supervisor: "Atlas",
    comingSoon: true
  },
  {
    id: "dashboard_viewed",
    day: 4,
    title: "Review Your Analytics",
    description: "See insights from your trial activity and ROI potential",
    icon: BarChart3,
    color: "purple",
    action: "View Analytics",
    path: "/trial/analytics",
    supervisor: null
  }
];

// Trial countdown component
const TrialCountdown = ({ daysRemaining, hoursRemaining, isExpired }) => {
  const getUrgencyColor = () => {
    if (isExpired) return "text-red-500";
    if (daysRemaining <= 1) return "text-orange-400";
    if (daysRemaining <= 2) return "text-amber-400";
    return "text-lime";
  };

  const getMessage = () => {
    if (isExpired) return "Trial expired! 24 hours to claim your spot";
    if (daysRemaining === 0) return `${hoursRemaining} hours remaining`;
    if (daysRemaining === 1) return "Last day of trial!";
    return `${daysRemaining} days remaining`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
        isExpired ? "bg-red-500/10 border-red-500/30" : "bg-gold/10 border-gold/30"
      }`}
    >
      <Clock className={`w-4 h-4 ${getUrgencyColor()}`} />
      <span className={`text-sm font-medium ${getUrgencyColor()}`}>
        {getMessage()}
      </span>
    </motion.div>
  );
};

// Mission card component
const MissionCard = ({ mission, isCompleted, isCurrent, isLocked, onStart, onComplete }) => {
  const Icon = mission.icon;

  const getStatusIcon = () => {
    if (mission.comingSoon) return <Lock className="w-5 h-5 text-slate-600" />;
    if (isCompleted) return <CheckCircle2 className="w-5 h-5 text-lime" />;
    if (isLocked) return <Lock className="w-5 h-5 text-slate-600" />;
    return <Circle className="w-5 h-5 text-slate-500" />;
  };

  const handleAction = () => {
    if (mission.comingSoon) return; // Non-actionable
    if (mission.phone) {
      window.open(`tel:${mission.phone.replace(/\D/g, '')}`, '_blank');
      setTimeout(() => onComplete(mission.id), 2000);
    } else if (mission.path) {
      onStart(mission.path);
    } else {
      onComplete(mission.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`relative p-5 rounded-xl border transition-all ${
        mission.comingSoon
          ? "bg-slate-800/20 border-slate-700/40 opacity-60 cursor-default"
          : isCompleted
            ? "bg-lime/5 border-lime/30"
            : isCurrent
              ? "bg-gold/5 border-gold/40 shadow-[0_0_20px_rgba(212,175,55,0.15)]"
              : isLocked
                ? "bg-slate-800/30 border-slate-700/50 opacity-60"
                : "bg-[#0A1244] border-gold/20"
      }`}
    >
      {/* Day indicator */}
      <div className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-indigo-700 border-2 border-gold flex items-center justify-center">
        <span className="text-xs font-bold text-gold">D{mission.day}</span>
      </div>

      <div className="flex items-start gap-4 ml-4">
        {/* Status */}
        <div className="mt-1">{getStatusIcon()}</div>
        
        {/* Icon */}
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
          mission.comingSoon ? "bg-slate-700/30" : isCompleted ? "bg-lime/20" : `bg-${mission.color}-500/20`
        }`}>
          <Icon className={`w-6 h-6 ${mission.comingSoon ? "text-slate-600" : isCompleted ? "text-lime" : `text-${mission.color}-400`}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-inter font-bold ${mission.comingSoon ? "text-slate-500" : isCompleted ? "text-lime" : "text-white"}`}>
              {mission.title}
            </h3>
            {mission.supervisor && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/20 text-gold">
                {mission.supervisor}
              </span>
            )}
            {mission.comingSoon && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-400 border border-slate-600">
                Coming Soon
              </span>
            )}
          </div>
          <p className="text-sm text-slate-400">{mission.description}</p>
          
          {mission.phone && !isCompleted && (
            <p className="text-xs text-gold mt-2 font-mono">{mission.phone}</p>
          )}
        </div>

        {/* Action */}
        {!mission.comingSoon && !isCompleted && !isLocked && (
          <Button
            onClick={handleAction}
            className={`shrink-0 ${
              isCurrent 
                ? "bg-gold hover:bg-gold/90 text-indigo-700" 
                : "bg-indigo-600 hover:bg-indigo-500 text-white"
            }`}
            data-testid={`mission-${mission.id}-btn`}
          >
            {mission.action}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
        
        {isCompleted && !mission.comingSoon && (
          <span className="text-xs text-lime font-medium">Completed</span>
        )}
      </div>
    </motion.div>
  );
};

// Savings preview component
const SavingsPreview = ({ savingsData }) => {
  if (!savingsData) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-br from-[#0A1244] to-[#060D3A] rounded-xl border border-gold/30 p-5"
    >
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-gold" />
        <h3 className="font-inter font-bold text-gold">Your Projected Savings</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 rounded-lg bg-gold/10">
          <p className="text-xs text-slate-400">Monthly</p>
          <p className="text-2xl font-inter font-black text-gold">
            ${savingsData.monthly_total?.toLocaleString() || 0}
          </p>
        </div>
        <div className="text-center p-3 rounded-lg bg-lime/10">
          <p className="text-xs text-slate-400">Annual</p>
          <p className="text-2xl font-inter font-black text-lime">
            ${((savingsData.monthly_total || 0) * 12).toLocaleString()}
          </p>
        </div>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-400">Compatibility Score</span>
        <span className="text-lime font-bold">{savingsData.compatibility_score || 0}%</span>
      </div>
    </motion.div>
  );
};

// Main Trial Dashboard
export default function TrialDashboard() {
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [remainingSpots, setRemainingSpots] = useState(50);
  const navigate = useNavigate();

  // Get trial token from storage
  const trialToken = sessionStorage.getItem("argus_trial_token");

  useEffect(() => {
    if (!trialToken) {
      navigate("/trial/login");
      return;
    }
    fetchTrialStatus();
    fetchRemainingSpots();
  }, [trialToken]);

  // Show expiration modal when trial is about to expire
  useEffect(() => {
    if (trialStatus) {
      const daysRemaining = trialStatus.days_remaining || 0;
      const isExpired = trialStatus.is_expired;
      
      // Show modal if: expired, or 1 day or less remaining, or user just logged in with low time
      if (isExpired || daysRemaining <= 1) {
        // Slight delay to let user see the dashboard first
        const timer = setTimeout(() => setShowExpirationModal(true), 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [trialStatus]);

  const fetchTrialStatus = async () => {
    try {
      const res = await axios.get(`${API}/trial/status`, {
        headers: { Authorization: `Bearer ${trialToken}` }
      });
      setTrialStatus(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        sessionStorage.removeItem("argus_trial_token");
        navigate("/trial/login");
      } else {
        setError(err.response?.data?.detail || "Failed to load trial status");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRemainingSpots = async () => {
    try {
      const res = await axios.get(`${API}/founding-spots`);
      setRemainingSpots(res.data.remaining || 50);
    } catch (err) {
      // Ignore error, use default
    }
  };

  const handleMissionComplete = async (missionId) => {
    try {
      await axios.post(`${API}/trial/update-progress?mission=${missionId}&completed=true`, null, {
        headers: { Authorization: `Bearer ${trialToken}` }
      });
      toast.success("Mission completed!");
      fetchTrialStatus();
    } catch (err) {
      toast.error("Failed to update progress");
    }
  };

  const handleStartMission = (path) => {
    navigate(path);
  };

  const handleBecomeFounder = () => {
    navigate("/trial/checkout");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#040F49] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#040F49] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
          <Button onClick={() => navigate("/")} className="mt-4">Return Home</Button>
        </div>
      </div>
    );
  }

  const progress = trialStatus?.onboarding_progress || {};
  // S003: Calculate from canonical mission keys — never include pos_ready (non-completable)
  const COMPLETABLE_MISSION_IDS = new Set(
    MISSIONS.filter(m => !m.comingSoon).map(m => m.id)
  );
  const missionsCompleted = Object.entries(progress).filter(
    ([key, val]) => val === true && COMPLETABLE_MISSION_IDS.has(key)
  ).length;
  const completableMissionCount = MISSIONS.filter(m => !m.comingSoon).length;
  const progressPercent = completableMissionCount > 0
    ? (missionsCompleted / completableMissionCount) * 100
    : 0;

  return (
    <div className="min-h-screen bg-[#040F49]">
      {/* Header */}
      <header className="border-b border-gold/10 bg-[#040F49]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <ShieldLogo size={36} />
              <div>
                <span className="font-inter font-bold text-gold text-lg">Argus Xenia</span>
                <span className="text-xs text-slate-500 block">Trial Dashboard</span>
              </div>
            </Link>

            <div className="flex items-center gap-4">
              <TrialCountdown 
                daysRemaining={trialStatus?.days_remaining || 0}
                hoursRemaining={trialStatus?.hours_remaining || 0}
                isExpired={trialStatus?.is_expired}
              />
              
              {!trialStatus?.is_expired ? (
                <Button
                  onClick={handleBecomeFounder}
                  className="bg-gold hover:bg-gold/90 text-indigo-700 font-bold"
                  data-testid="become-founder-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Become a Founder
                </Button>
              ) : (
                <Button
                  onClick={handleBecomeFounder}
                  className="bg-red-500 hover:bg-red-600 text-white font-bold animate-pulse"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Claim Now (24h left)
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-3xl font-inter font-black text-white mb-2">
            Welcome, {trialStatus?.restaurant_name || "Chef"}!
          </h1>
          <p className="text-slate-400">
            Complete these missions to discover how Argus Xenia can transform your restaurant.
          </p>
        </motion.div>

        {/* Progress bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Onboarding Progress</span>
            <span className="text-sm font-bold text-gold">{missionsCompleted}/{completableMissionCount} Missions</span>
          </div>
          <Progress value={progressPercent} className="h-3" data-testid="trial-progress-bar" />
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Missions list */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-lg font-inter font-bold text-white flex items-center gap-2 mb-4">
              <Play className="w-5 h-5 text-gold" />
              Your Guided Tour
            </h2>
            
            {MISSIONS.map((mission, idx) => {
              const isCompleted = !mission.comingSoon && (progress[mission.id] || false);
              const prevCompletable = MISSIONS.slice(0, idx).filter(m => !m.comingSoon);
              const allPrevDone = prevCompletable.every(m => progress[m.id]);
              const isCurrent = !mission.comingSoon && !isCompleted && allPrevDone;
              const isLocked = !mission.comingSoon && !isCompleted && !isCurrent;
              
              return (
                <MissionCard
                  key={mission.id}
                  mission={mission}
                  isCompleted={isCompleted}
                  isCurrent={isCurrent}
                  isLocked={isLocked}
                  onStart={handleStartMission}
                  onComplete={handleMissionComplete}
                />
              );
            })}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Savings preview */}
            <SavingsPreview savingsData={trialStatus?.savings_report} />

            {/* Quick links */}
            <div className="bg-[#0A1244] rounded-xl border border-gold/20 p-5">
              <h3 className="font-inter font-bold text-white mb-4">Quick Access</h3>
              <div className="space-y-2">
                <Link
                  to="/trial/sop-builder"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gold/5 transition-colors group"
                >
                  <ChefHat className="w-5 h-5 text-orange-400" />
                  <span className="text-sm text-slate-300 group-hover:text-white">SOP Builder</span>
                  <ExternalLink className="w-4 h-4 text-slate-600 ml-auto" />
                </Link>
                <Link
                  to="/trial/vision"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gold/5 transition-colors group"
                >
                  <Eye className="w-5 h-5 text-blue-400" />
                  <span className="text-sm text-slate-300 group-hover:text-white">Vision AI</span>
                  <ExternalLink className="w-4 h-4 text-slate-600 ml-auto" />
                </Link>
                {process.env.REACT_APP_ECHO_PHONE && (
                  <a
                    href={`tel:${process.env.REACT_APP_ECHO_PHONE}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gold/5 transition-colors group"
                  >
                    <Phone className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm text-slate-300 group-hover:text-white">Call Echo</span>
                    <ExternalLink className="w-4 h-4 text-slate-600 ml-auto" />
                  </a>
                )}
              </div>
            </div>

            {/* Help section */}
            <div className="text-center p-4 rounded-xl border border-slate-700 bg-slate-800/30">
              <p className="text-sm text-slate-400 mb-2">Need help?</p>
              <p className="text-xs text-slate-500">
                Our AI Argus is always available on the landing page to answer questions.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Trial Expiration Modal */}
      <TrialExpirationModal
        isOpen={showExpirationModal}
        onClose={() => setShowExpirationModal(false)}
        hoursRemaining={trialStatus?.hours_remaining || 0}
        daysRemaining={trialStatus?.days_remaining || 0}
        isExpired={trialStatus?.is_expired || false}
        restaurantName={trialStatus?.restaurant_name || "Chef"}
        savingsEstimate={trialStatus?.savings_report?.monthly_total || 0}
        remainingSpots={remainingSpots}
      />
    </div>
  );
}
